import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  Button,
  Autocomplete,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  Alert
} from '@mui/material';
import {
  FaPlus,
  FaTimes,
  FaStar,
  FaChevronDown,
  FaChevronUp,
  FaLightbulb
} from 'react-icons/fa';
import './SkillSelector.css';

const SkillSelector = ({ skills, onAddSkill, onRemoveSkill }) => {
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [otherSkill, setOtherSkill] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('Intermédiaire');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [otherValidationError, setOtherValidationError] = useState('');
  
  const toSlug = (value) => {
    if (!value) return '';
    return value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/['’]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_+/g, '_');
  };

  const sortLabels = (items) =>
    [...items].sort((a, b) =>
      a.localeCompare(b, 'fr', { sensitivity: 'base' })
    );

  const skillSuggestions = {
    'Compétences techniques sportives': sortLabels([
      'Animation de séances sportives',
      'Conduite de séance pédagogique',
      'Développement technique individuel',
      'Observation et correction des gestes',
      'Organisation de situations d’apprentissage',
      'Planification des cycles d’entraînement',
      'Préparation de séances tous publics',
      'Spécialisation par poste / rôle',
      'Transmission des fondamentaux sportifs'
    ]),
    'Compétences tactiques et stratégiques': sortLabels([
      'Analyse de jeu',
      'Animation de projet de jeu',
      'Lecture tactique',
      'Mise en place de principes de jeu',
      'Organisation défensive',
      'Organisation offensive',
      'Préparation des compétitions',
      'Stratégie collective',
      'Vidéo-analyse'
    ]),
    'Compétences physiques et performance': sortLabels([
      'Développement des qualités physiques',
      'Échauffement et récupération',
      'Évaluation physique',
      'Gestion de la charge d’entraînement',
      'Préparation physique générale',
      'Préparation physique spécifique',
      'Prévention des blessures',
      'Suivi de la performance'
    ]),
    'Compétences mentales et humaines': sortLabels([
      'Accompagnement psychosocial',
      'Cohésion d’équipe',
      'Communication avec les publics',
      'Confiance et estime de soi',
      'Gestion du stress',
      'Leadership',
      'Motivation des pratiquants',
      'Relation éducative'
    ]),
    'Compétences pédagogiques et éducatives': sortLabels([
      'Adaptation aux publics (âge, niveau)',
      'Conception de contenus pédagogiques',
      'Encadrement des jeunes',
      'Éducation par le sport',
      'Gestion de groupe',
      'Pédagogie différenciée',
      'Transmission des valeurs sportives'
    ]),
    'Compétences santé, sécurité et inclusion': sortLabels([
      'Accueil de publics en situation de handicap',
      'Activité physique adaptée (APA)',
      'Gestes de premiers secours',
      'Prévention des conduites à risque',
      'Respect des protocoles médicaux',
      'Sécurité des pratiquants',
      'Sport santé'
    ]),
    'Compétences organisationnelles et gestion': sortLabels([
      'Coordination d’équipes',
      'Gestion de planning',
      'Organisation d’événements sportifs',
      'Reporting et suivi d’activité',
      'Suivi administratif des missions'
    ]),
    'Compétences numériques et outils': sortLabels([
      'Outils collaboratifs (Drive, agenda partagé)',
      'Outils de suivi de performance',
      'Plateformes de visioconférence',
      'Rédaction de bilans numériques',
      'Utilisation d’outils de planification'
    ]),
    Autre: ['Autre compétence (à préciser)']
  };
  
  const skillLevels = [
    { value: 'Débutant', color: '#ff9800', stars: 1 },
    { value: 'Intermédiaire', color: '#2196f3', stars: 2 },
    { value: 'Avancé', color: '#4caf50', stars: 3 },
    { value: 'Expert', color: '#9c27b0', stars: 4 }
  ];
  
  const allOptions = Object.entries(skillSuggestions).flatMap(
    ([category, list]) =>
      list.map((label) => ({
        label,
        category,
        slug: label === 'Autre compétence (à préciser)' ? 'autre' : toSlug(label)
      }))
  );
  
  const availableOptions = allOptions.filter((option) => {
    if (option.slug === 'autre') return true;
    return !skills.some(
      (skill) => (skill.slug || toSlug(skill.name)) === option.slug
    );
  });

  const availableSuggestions = Object.fromEntries(
    Object.entries(skillSuggestions).map(([category, list]) => [
      category,
      list.filter((label) => {
        const slug = label === 'Autre compétence (à préciser)' ? 'autre' : toSlug(label);
        if (slug === 'autre') return false;
        return !skills.some((skill) => (skill.slug || toSlug(skill.name)) === slug);
      })
    ])
  );
  
  // Ajouter une nouvelle compétence
  const handleAddSkill = () => {
    if (!selectedSkill) {
      setValidationError('Veuillez sélectionner une compétence');
      return;
    }
    
    const isOther = selectedSkill.slug === 'autre';
    const finalName = isOther ? otherSkill.trim() : selectedSkill.label;

    if (isOther && !finalName) {
      setOtherValidationError('Veuillez préciser la compétence');
      return;
    }

    const finalSlug = toSlug(finalName);
    const skillExists = skills.some((skill) => {
      const existingSlug = skill.slug || toSlug(skill.name);
      return existingSlug === finalSlug;
    });
    
    if (skillExists) {
      setValidationError('Cette compétence est déjà ajoutée');
      return;
    }
    
    const skill = {
      id: Date.now().toString(),
      name: finalName,
      slug: finalSlug,
      level: selectedLevel,
      category: isOther ? 'Autre' : selectedSkill.category,
      needsAdminValidation: isOther
    };
    
    onAddSkill(skill);
    setSelectedSkill(null);
    setOtherSkill('');
    setValidationError('');
    setOtherValidationError('');
  };
  
  // Déterminer la catégorie d'une compétence
  const getCategoryForSkill = (skillName) => {
    for (const [category, skillList] of Object.entries(skillSuggestions)) {
      if (skillList.some(s => s.toLowerCase() === skillName.toLowerCase())) {
        return category;
      }
    }
    return 'Autre';
  };
  
  // Ajouter une compétence suggérée
  const handleAddSuggestion = (suggestion) => {
    const skill = {
      id: Date.now().toString(),
      name: suggestion,
      slug: toSlug(suggestion),
      level: 'Intermédiaire',
      category: getCategoryForSkill(suggestion)
    };
    
    onAddSkill(skill);
  };
  
  // Supprimer une compétence
  const handleRemoveSkill = (skillId) => {
    onRemoveSkill(skillId);
  };
  
  // Obtenir la couleur du niveau
  const getLevelColor = (level) => {
    const levelData = skillLevels.find(l => l.value === level);
    return levelData ? levelData.color : '#757575';
  };
  
  // Obtenir le nombre d'étoiles pour le niveau
  const getLevelStars = (level) => {
    const levelData = skillLevels.find(l => l.value === level);
    return levelData ? levelData.stars : 1;
  };
  
  // Grouper les compétences par catégorie
  const groupedSkills = skills.reduce((groups, skill) => {
    const category = skill.category || 'Autre';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(skill);
    return groups;
  }, {});
  
  return (
    <Box className="skill-selector">
      <Typography variant="h6" className="section-title">
        <FaLightbulb className="section-icon" />
        Compétences et expertises
      </Typography>
      
      {/* Formulaire d'ajout */}
      <Paper className="add-skill-form" elevation={0}>
        <Typography variant="subtitle1" className="form-title">
          Ajouter une compétence
        </Typography>
        
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={availableOptions}
              groupBy={(option) => option.category}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.slug === value.slug}
              value={selectedSkill}
              onChange={(event, newValue) => {
                setSelectedSkill(newValue);
                setValidationError('');
                if (newValue?.slug !== 'autre') {
                  setOtherSkill('');
                  setOtherValidationError('');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Nom de la compétence"
                  variant="outlined"
                  error={!!validationError}
                  helperText={validationError}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSkill();
                    }
                  }}
                />
              )}
              className="skill-input"
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={skillLevels}
              getOptionLabel={(option) => option.value}
              value={skillLevels.find(level => level.value === selectedLevel)}
              onChange={(event, newValue) => {
                setSelectedLevel(newValue ? newValue.value : 'Intermédiaire');
              }}
              renderOption={(props, option) => (
                <Box {...props} className="level-option">
                  <Box className="level-stars">
                    {[...Array(option.stars)].map((_, i) => (
                      <FaStar key={i} style={{ color: option.color }} />
                    ))}
                  </Box>
                  <span>{option.value}</span>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Niveau"
                  variant="outlined"
                />
              )}
              className="level-select"
            />
          </Grid>
          
          <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'center' }}>
            <IconButton
              onClick={handleAddSkill}
              className="add-button"
              sx={{ 
                backgroundColor: 'primary.main', 
                color: 'white',
                '&:hover': { backgroundColor: 'primary.dark' },
                width: 56,
                height: 56
              }}
            >
              <FaPlus />
            </IconButton>
          </Grid>
        </Grid>

        {selectedSkill?.slug === 'autre' && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Précisez la compétence"
              value={otherSkill}
              onChange={(e) => {
                setOtherSkill(e.target.value);
                setOtherValidationError('');
              }}
              error={!!otherValidationError}
              helperText={
                otherValidationError || 'Cette compétence sera soumise à validation admin'
              }
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddSkill();
                }
              }}
            />
          </Box>
        )}
      </Paper>
      
      {/* Suggestions */}
      <Box className="suggestions-section">
        <Button
          variant="text"
          onClick={() => setShowSuggestions(!showSuggestions)}
          endIcon={showSuggestions ? <FaChevronUp /> : <FaChevronDown />}
          className="suggestions-toggle"
        >
          Suggestions de compétences (
          {Object.values(availableSuggestions).reduce((acc, cur) => acc + cur.length, 0)}
          )
        </Button>
        
        <Collapse in={showSuggestions}>
          <Box className="suggestions-grid">
            {Object.entries(skillSuggestions).map(([category, categorySkills]) => {
              const availableInCategory = availableSuggestions[category] || [];
              
              if (availableInCategory.length === 0) return null;
              
              return (
                <Box key={category} className="suggestion-category">
                  <Typography variant="subtitle2" className="category-title">
                    {category}
                  </Typography>
                  <Box className="category-skills">
                    {availableInCategory.map((skill) => (
                      <Chip
                        key={skill}
                        label={skill}
                        onClick={() => handleAddSuggestion(skill)}
                        className="suggestion-chip"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>
      
      {/* Liste des compétences ajoutées */}
      {skills.length > 0 ? (
        <Box className="skills-list">
          <Typography variant="subtitle1" className="list-title">
            Mes compétences ({skills.length})
          </Typography>
          
          {Object.entries(groupedSkills).map(([category, categorySkills]) => (
            <Box key={category} className="skill-category">
              <Typography variant="subtitle2" className="category-header">
                {category} ({categorySkills.length})
              </Typography>
              
              <Box className="skills-grid">
                {categorySkills.map((skill) => (
                  <Paper key={skill.id} className="skill-item" elevation={1}>
                    <Box className="skill-content">
                      <Typography variant="body1" className="skill-name">
                        {skill.name}
                      </Typography>
                      
                      <Box className="skill-level">
                        <Box className="level-stars">
                          {[...Array(getLevelStars(skill.level))].map((_, i) => (
                            <FaStar 
                              key={i} 
                              style={{ color: getLevelColor(skill.level) }}
                              className="star-icon"
                            />
                          ))}
                        </Box>
                        <Typography 
                          variant="caption" 
                          className="level-text"
                          style={{ color: getLevelColor(skill.level) }}
                        >
                          {skill.level}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Tooltip title="Supprimer cette compétence">
                      <IconButton
                        onClick={() => handleRemoveSkill(skill.id)}
                        className="remove-button"
                        size="small"
                      >
                        <FaTimes />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Alert severity="info" className="empty-state">
          <Typography variant="body2">
            Aucune compétence ajoutée. Commencez par ajouter vos principales expertises.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default SkillSelector;
